import React, { useState, useEffect } from "react";
import { X, Clock, CheckCircle2, Sliders, AlertCircle, Send } from "lucide-react";
import axios from "axios";
import { baseUrl, environment } from "../../environment";
import { useSelector } from "react-redux";
import { StoreState } from "../../models/reduxModels";
import { useSnackbar } from "notistack";
import moment from "moment";

interface IPhoneApprovalModalProps {
  open: boolean;
  onClose: () => void;
  approvalDateStr: string;
  targetItem?: any;
  onSuccess?: () => void;
}

const IPhoneApprovalModal: React.FC<IPhoneApprovalModalProps> = ({
  open,
  onClose,
  approvalDateStr,
  targetItem,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const userDetails = useSelector((state: StoreState) => state.user.userDetails);

  // flag: 2 = "Mark as Present", 1 = "Mark Exact Time", 0 = unselected
  const [flag, setFlag] = useState<number>(2);
  const [inTime, setInTime] = useState<string>("09:30");
  const [outTime, setOutTime] = useState<string>("18:30");
  const [remarks, setRemarks] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFlag(2);
      setErrorMsg("");
      setRemarks("");

      const origIn = targetItem?.original?.upd_in_time || targetItem?.original?.in_time;
      const origOut = targetItem?.original?.upd_out_time || targetItem?.original?.out_time;

      const formattedIn = origIn && moment(origIn, ["HH:mm:ss", "hh:mm A", "HH:mm"]).isValid()
        ? moment(origIn, ["HH:mm:ss", "hh:mm A", "HH:mm"]).format("HH:mm")
        : "09:30";

      const formattedOut = origOut && moment(origOut, ["HH:mm:ss", "hh:mm A", "HH:mm"]).isValid()
        ? moment(origOut, ["HH:mm:ss", "hh:mm A", "HH:mm"]).format("HH:mm")
        : "18:30";

      setInTime(formattedIn);
      setOutTime(formattedOut);
    }
  }, [open, targetItem]);

  if (!open) return null;

  const rawId =
    targetItem?.original?.daily_atten_id ||
    targetItem?.daily_atten_id ||
    (Number.isInteger(Number(targetItem?.id)) ? targetItem?.id : null);
  const numericAttendanceId =
    rawId !== undefined && rawId !== null && !isNaN(Number(rawId)) && Number(rawId) > 0
      ? Number(rawId)
      : null;

  const isTimeInvalid = flag === 1 && inTime && outTime && outTime <= inTime;

  const handleSend = async () => {
    setErrorMsg("");

    if (!remarks.trim()) {
      setErrorMsg("Please enter reason for your approval.");
      return;
    }

    if (flag === 1 && isTimeInvalid) {
      setErrorMsg("Out time must be after in time");
      return;
    }

    const formattedDate = approvalDateStr
      ? moment(approvalDateStr, ["YYYY-MM-DD", moment.ISO_8601]).isValid()
        ? moment(approvalDateStr).format("YYYY-MM-DD")
        : approvalDateStr
      : null;

    if (!formattedDate) {
      setErrorMsg("Invalid attendance date.");
      return;
    }

    if (!numericAttendanceId) {
      setErrorMsg(
        "Attendance regularization requires a recorded attendance punch (Half-Day / Missed Out-Time). For absent days, please apply for Leave."
      );
      return;
    }

    setSubmitting(true);

    try {
      let formattedInTime: string | null = null;
      let formattedOutTime: string | null = null;

      if (flag === 1) {
        formattedInTime = moment(inTime, "HH:mm").format("hh:mm A");
        formattedOutTime = moment(outTime, "HH:mm").format("hh:mm A");
      }

      const payload = {
        attendanceId: numericAttendanceId,
        inTime: formattedInTime,
        outTime: formattedOutTime,
        attendanceDate: formattedDate,
        remarks: remarks.trim(),
      };

      const response = await axios.post(
        baseUrl + environment.urls.sendForApproval,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userDetails?.accessToken}`,
          },
        }
      );

      setSubmitting(false);
      if (response.status === 200) {
        enqueueSnackbar("Approval request submitted successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        onSuccess?.();
        onClose();
      } else {
        setErrorMsg(response.data?.message || "Failed to submit approval request.");
      }
    } catch (err: any) {
      setSubmitting(false);
      const backendError =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        (err?.message && !err.message.startsWith("Request failed with status code") ? err.message : null) ||
        "Failed to submit approval request.";
      setErrorMsg(backendError);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 animate-fadeIn overflow-y-auto">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 border border-gray-100 shadow-2xl space-y-4 text-gray-900 max-h-[90dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#003366] flex items-center justify-center font-bold">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#003366]">Attendance Marking</h3>
              <p className="text-[11px] text-gray-400 font-medium">Date: {approvalDateStr}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Global Guidance Notice for Absent Days without Recorded Punch */}
        {!numericAttendanceId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl space-y-1">
            <p className="text-xs font-bold text-amber-800">No Recorded Attendance</p>
            <p className="text-[11px] text-amber-700 leading-relaxed">
              Attendance regularization is only available for days with recorded punches (Half-Day / Missed Out-Time). For full absent days, please apply for Leave through the Leave portal.
            </p>
          </div>
        )}

        {/* Global Error Alert */}
        {errorMsg && (
          <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 text-xs font-semibold flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Action Toggle Mode Selection */}
        <div className="grid grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={() => {
              setFlag(2);
              setErrorMsg("");
            }}
            className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center space-x-2 transition-all active:scale-95 ${
              flag === 2
                ? "bg-[#e0f7fa] border-[#2196F3] text-[#003366] shadow-xs"
                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            }`}
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Mark as Present</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setFlag(1);
              setErrorMsg("");
            }}
            className={`p-3 rounded-2xl border text-xs font-bold flex items-center justify-center space-x-2 transition-all active:scale-95 ${
              flag === 1
                ? "bg-[#e0f7fa] border-[#2196F3] text-[#003366] shadow-xs"
                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            }`}
          >
            <Sliders className="w-4 h-4 text-[#2196F3] shrink-0" />
            <span>Mark Exact Time</span>
          </button>
        </div>

        {/* Exact Time Pickers (Only if Flag === 1) */}
        {flag === 1 && (
          <div className="bg-gray-50/80 p-3.5 rounded-2xl border border-gray-200/80 space-y-2">
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                  In Time
                </label>
                <input
                  type="time"
                  value={inTime}
                  onChange={(e) => {
                    setInTime(e.target.value);
                    setErrorMsg("");
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:border-[#2196F3] shadow-xs"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                  Out Time
                </label>
                <input
                  type="time"
                  value={outTime}
                  onChange={(e) => {
                    setOutTime(e.target.value);
                    setErrorMsg("");
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-bold text-gray-900 focus:outline-none focus:border-[#2196F3] shadow-xs"
                />
              </div>
            </div>

            {isTimeInvalid && (
              <p className="text-[11px] font-semibold text-rose-500 mt-1">
                Out time must be after in time
              </p>
            )}
          </div>
        )}

        {/* Remarks Input */}
        <div>
          <textarea
            rows={3}
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              if (e.target.value.trim() !== "") setErrorMsg("");
            }}
            placeholder="Remarks..."
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2196F3]"
          />
        </div>

        {/* Submit Send Button */}
        <button
          type="button"
          onClick={handleSend}
          disabled={submitting || flag === 0 || !numericAttendanceId}
          className={`w-full py-3 rounded-2xl text-white text-xs font-bold shadow-md flex items-center justify-center space-x-2 transition-all active:scale-95 ${
            flag > 0 && !!numericAttendanceId ? "bg-[#2196F3] hover:bg-[#1976D2]" : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          {submitting ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Send</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default IPhoneApprovalModal;
